'use client';

import {
    ChevronIcon,
    CInstaClubVector,
    CloseIcon,
    DashboardIcon,
    MessageQuestionIcon,
    MonitorIcon,
    ShieldIcon,
    TermsAndConditionsIcon
} from '@insta-club/components/icons';
import { useDrawerStore, useSessionStore } from '@insta-club/state';
import { Avatar } from '@insta-club/ui';
import Image from 'next/image';
import Link from 'next/link';
import type React from 'react';
import { useEffect } from 'react';

export function Drawer() {
    const { isOpen, closeDrawer } = useDrawerStore();
    const { session } = useSessionStore();

    useEffect(() => {
        if (!isOpen) return;

        const handlePopState = () => {
            closeDrawer();
            history.pushState(null, '', location.href);
        };

        history.pushState(null, '', location.href);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, closeDrawer]);

    return (
        <>
            {isOpen && <div className='fixed inset-0 z-40 bg-black/50 transition-opacity' onClick={closeDrawer} />}
            <div
                className='pointer-events-none fixed top-0 left-1/2 z-50 h-full w-full max-w-[var(--container-max)] overflow-hidden'
                style={{ transform: 'translateX(-50%)' }}
            >
                <div
                    className={`absolute top-0 right-0 h-full w-80 bg-white transition-transform duration-300 ease-in-out ${isOpen ? 'pointer-events-auto translate-x-0' : 'pointer-events-none translate-x-full'}`}
                >
                    <div className='flex h-full flex-col' dir='rtl'>
                        <div className='flex items-center justify-between p-4'>
                            {/* <InstaClubTypoLogo /> */}
                            <CInstaClubVector width={120} />
                            <button
                                onClick={closeDrawer}
                                className='rounded-lg p-2 transition-colors hover:bg-gray-100'
                            >
                                <i className='h-5 w-5'></i>
                                <CloseIcon />
                            </button>
                        </div>

                        <Link
                            onClick={() => {
                                closeDrawer();
                            }}
                            href={'/profile/account'}
                        >
                            <div className='bg-primary-50 flex items-center justify-between px-6 py-3'>
                                <div>
                                    <div className='flex items-center gap-3'>
                                        <Avatar
                                            src={session?.image ?? '/assets/images/default-profile.png'}
                                            alt='user-profile'
                                            size={48}
                                            className='rounded-xl'
                                        />
                                        <div className='flex-1'>
                                            <div className='font-semibold text-gray-900'>
                                                {session?.first_name} {session?.last_name}
                                            </div>
                                            <div className='text-sm text-gray-500'>{session?.mobile}</div>
                                        </div>
                                    </div>
                                </div>
                                <ChevronIcon className='text-gray-500' />
                            </div>
                        </Link>

                        <div className='flex-1'>
                            <div className='pr-6'>
                                <MenuItem
                                    action={() => closeDrawer()}
                                    href='/dashboard'
                                    icon={<DashboardIcon className='h-5 w-5' />}
                                    label='داشبورد'
                                />
                                <MenuItem
                                    action={() => closeDrawer()}
                                    href='/tour'
                                    icon={<MonitorIcon className='h-5 w-5' />}
                                    label='تور آموزشی'
                                />
                                <MenuItem
                                    action={() => closeDrawer()}
                                    href='/faq'
                                    icon={<MessageQuestionIcon className='h-5 w-5' />}
                                    label='سوالات متداول'
                                />
                                <MenuItem
                                    action={() => closeDrawer()}
                                    href='/terms'
                                    icon={<TermsAndConditionsIcon className='h-5 w-5' />}
                                    label='شرایط و قوانین'
                                />
                                <MenuItem
                                    action={() => closeDrawer()}
                                    href='/privacy'
                                    icon={<ShieldIcon className='h-5 w-5' />}
                                    label='حریم خصوصی'
                                />
                            </div>
                        </div>

                        <div className='space-y-1 p-4 text-center text-xs font-normal text-gray-800'>
                            <div className='flex items-center justify-center space-x-2'>
                                <Image
                                    src={'/assets/images/sepehr-icon.png'}
                                    width={20}
                                    height={20}
                                    alt='sepehr-icon'
                                />
                                <p className='text-xs font-medium text-gray-500'>
                                    پیاده سازی توسط{' '}
                                    <Link href={'https://sepehr.it'} target='_blank' className='text-gray-800'>
                                        فناوری اطلاعات سپهر
                                    </Link>
                                </p>
                            </div>
                            <div style={{ fontFamily: 'Poppins' }}>Version 1.0.4</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

interface MenuItemProps {
    href: string;
    icon: React.ReactNode;
    label: string;
    badge?: number;
    value?: React.ReactNode;
    action?: () => void;
}

function MenuItem({ icon, label, badge, value, href, action }: MenuItemProps) {
    return (
        <Link
            onClick={() => action?.()}
            href={href}
            className='group flex cursor-pointer items-center justify-between border-b border-b-gray-100 px-4 py-3.5 transition-colors hover:bg-gray-100'
        >
            <div className='flex items-center gap-3'>
                <div className='group-hover:text-primary-600 text-gray-600 transition-colors'>{icon}</div>
                <span className='group-hover:text-primary-600 text-base font-semibold text-gray-600 transition-colors'>
                    {label}
                </span>
            </div>
            <div className='flex items-center gap-2'>
                {value && value}
                {!!badge && (
                    <span className='bg-secondary-50 text-secondary-500 min-w-[20px] rounded-sm px-2 py-1 text-center text-[13px] font-semibold'>
                        {badge}
                    </span>
                )}
            </div>
        </Link>
    );
}

